import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { conDosDecimales } from '../venta/PiezasDeVenta';

interface Ejercicio {
  readonly id: string;
  readonly nombre: string;
  readonly desde: string;
  readonly hasta: string;
  readonly estado: string;
  readonly cerradoEn: string | null;
}

interface CuentaALaVista {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly activa: boolean;
  readonly hijas: number;
}

interface RenglonEnEdicion {
  cuentaId: string;
  lado: 'debe' | 'haber';
  importe: string;
}

const RENGLONES_VACIOS: RenglonEnEdicion[] = [
  { cuentaId: '', lado: 'debe', importe: '' },
  { cuentaId: '', lado: 'haber', importe: '' },
];

/** Suma una columna en centavos enteros: es plata, y acumular con coma flotante deja un centavo. */
function enCentavos(importe: string): number {
  const [entera = '0', fraccion = ''] = importe.replace(',', '.').split('.');
  const enteros = Number(entera);
  if (!Number.isFinite(enteros)) return 0;
  return enteros * 100 + Number(fraccion.padEnd(2, '0').slice(0, 2));
}

/**
 * El ejercicio y el asiento manual.
 *
 * Están en la misma pantalla porque son **las dos cosas que una persona le hace a la contabilidad
 * directamente**, sin una operación del comercio detrás. Todo lo demás aparece solo.
 *
 * Lo que la pantalla tiene que hacer bien es una sola cosa: **que quien escribe un asiento a mano
 * vea si cuadra antes de mandarlo**. El servidor lo rechaza igual, pero descubrirlo después de
 * cargar seis renglones es la manera más rápida de que alguien deje de usar la pantalla.
 */
export function Ejercicios(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const [abriendo, setAbriendo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [desde, setDesde] = useState(`${String(new Date().getFullYear())}-01-01`);
  const [hasta, setHasta] = useState(`${String(new Date().getFullYear())}-12-31`);

  const [asentando, setAsentando] = useState(false);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [glosa, setGlosa] = useState('');
  const [renglones, setRenglones] = useState<RenglonEnEdicion[]>(RENGLONES_VACIOS);

  const [reabriendo, setReabriendo] = useState<Ejercicio | null>(null);
  const [motivo, setMotivo] = useState('');

  const consulta = useQuery({
    queryKey: ['ejercicios'],
    queryFn: () => pedir<Ejercicio[]>('/ejercicios'),
  });

  const cuentas = useQuery({
    queryKey: ['cuentas-contables'],
    queryFn: () => pedir<CuentaALaVista[]>('/cuentas-contables'),
  });

  const imputables = (cuentas.data ?? []).filter((cuenta) => cuenta.hijas === 0 && cuenta.activa);

  function refrescar(): void {
    void cola.invalidateQueries({ queryKey: ['ejercicios'] });
    void cola.invalidateQueries({ queryKey: ['asientos'] });
  }

  const abrir = useMutation({
    mutationFn: () =>
      pedir<{ id: string; conApertura: boolean }>('/ejercicios', {
        method: 'POST',
        cuerpo: { nombre, desde, hasta },
      }),
    onSuccess: () => {
      setAbriendo(false);
      setNombre('');
      refrescar();
    },
  });

  const cerrar = useMutation({
    mutationFn: (id: string) =>
      pedir<{ resultado: string }>(`/ejercicios/${id}/cierre`, { method: 'POST' }),
    onSuccess: refrescar,
  });

  const reabrir = useMutation({
    mutationFn: (id: string) =>
      pedir<{ revertidos: number }>(`/ejercicios/${id}/reapertura`, {
        method: 'POST',
        cuerpo: { motivo },
      }),
    onSuccess: () => {
      setReabriendo(null);
      setMotivo('');
      refrescar();
    },
  });

  const asentar = useMutation({
    mutationFn: () =>
      pedir<{ numero: string }>('/asientos-manuales', {
        method: 'POST',
        cuerpo: {
          fecha,
          glosa,
          renglones: renglones
            .filter((renglon) => renglon.cuentaId !== '' && renglon.importe !== '')
            .map((renglon) => ({ ...renglon, importe: renglon.importe.replace(',', '.') })),
        },
      }),
    onSuccess: () => {
      setAsentando(false);
      setGlosa('');
      setRenglones(RENGLONES_VACIOS);
      refrescar();
    },
  });

  const totalDebe = renglones
    .filter((renglon) => renglon.lado === 'debe')
    .reduce((suma, renglon) => suma + enCentavos(renglon.importe), 0);
  const totalHaber = renglones
    .filter((renglon) => renglon.lado === 'haber')
    .reduce((suma, renglon) => suma + enCentavos(renglon.importe), 0);
  const cuadra = totalDebe === totalHaber && totalDebe > 0;

  if (consulta.isPending) return <Cargando />;
  if (consulta.isError) return <Problema error={consulta.error} />;

  const columnas: Columna<Ejercicio>[] = [
    { clave: 'nombre', titulo: t('ejercicios.nombre'), contenido: (fila) => fila.nombre },
    {
      clave: 'periodo',
      titulo: t('ejercicios.periodo'),
      contenido: (fila) => `${fila.desde} — ${fila.hasta}`,
    },
    {
      clave: 'estado',
      titulo: t('ejercicios.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.estado === 'cerrado' ? 'neutro' : 'bien'}>
          {t(`ejercicios.estados.${fila.estado}`)}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) =>
        fila.estado === 'abierto' ? (
          <Boton
            cargando={cerrar.isPending}
            onClick={() => {
              cerrar.mutate(fila.id);
            }}
          >
            {t('ejercicios.cerrar')}
          </Boton>
        ) : (
          <Boton
            onClick={() => {
              setReabriendo(fila);
            }}
          >
            {t('ejercicios.reabrir')}
          </Boton>
        ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <Encabezado
        titulo={t('ejercicios.titulo')}
        descripcion={t('ejercicios.descripcion')}
        acciones={
          <>
            <Boton
              onClick={() => {
                setAsentando(true);
              }}
            >
              {t('ejercicios.asientoManual')}
            </Boton>
            <Boton
              tono="principal"
              onClick={() => {
                setAbriendo(true);
              }}
            >
              {t('ejercicios.abrir')}
            </Boton>
          </>
        }
      />

      {cerrar.isError ? <Problema error={cerrar.error} /> : null}

      {cerrar.data === undefined ? null : (
        <Tarjeta>
          {/* En la unidad de la moneda, como todo el resto: el servidor devuelve cuatro decimales
              porque la columna los tiene, pero no existe el resultado de media centésima. */}
          <p className="text-sm">
            {t('ejercicios.cerrado', { resultado: conDosDecimales(cerrar.data.resultado) })}
          </p>
        </Tarjeta>
      )}

      <Tarjeta>
        {consulta.data.length === 0 ? (
          <Vacio titulo={t('ejercicios.vacio')} />
        ) : (
          <Tabla filas={consulta.data} columnas={columnas} claveDeFila={(fila) => fila.id} />
        )}
      </Tarjeta>

      <Dialogo
        abierto={abriendo}
        titulo={t('ejercicios.abrir')}
        onCerrar={() => {
          setAbriendo(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={abrir.isPending}
            disabled={nombre.trim() === ''}
            onClick={() => {
              abrir.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-texto-tenue">
            {t('ejercicios.nombre')}
            <input
              value={nombre}
              onChange={(evento) => {
                setNombre(evento.target.value);
              }}
              className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
            />
          </label>
          <div className="flex gap-3">
            <label className="flex flex-col gap-1 text-xs text-texto-tenue">
              {t('ejercicios.desde')}
              <input
                type="date"
                value={desde}
                onChange={(evento) => {
                  setDesde(evento.target.value);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-texto-tenue">
              {t('ejercicios.hasta')}
              <input
                type="date"
                value={hasta}
                onChange={(evento) => {
                  setHasta(evento.target.value);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              />
            </label>
          </div>
          {abrir.isError ? <p className="text-xs text-mal">{textoDeError(abrir.error)}</p> : null}
        </div>
      </Dialogo>

      <Dialogo
        abierto={asentando}
        titulo={t('ejercicios.asientoManual')}
        descripcion={t('ejercicios.asientoManualAyuda')}
        onCerrar={() => {
          setAsentando(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={asentar.isPending}
            disabled={!cuadra || glosa.trim() === ''}
            onClick={() => {
              asentar.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <label className="flex flex-col gap-1 text-xs text-texto-tenue">
              {t('ejercicios.fecha')}
              <input
                type="date"
                value={fecha}
                onChange={(evento) => {
                  setFecha(evento.target.value);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              />
            </label>
            <label className="flex flex-1 flex-col gap-1 text-xs text-texto-tenue">
              {t('ejercicios.glosa')}
              <input
                value={glosa}
                onChange={(evento) => {
                  setGlosa(evento.target.value);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              />
            </label>
          </div>

          {renglones.map((renglon, indice) => (
            // El índice como clave: los renglones no tienen identidad propia hasta que se
            // guardan, y reordenarlos no es una operación de esta pantalla.
            <div key={indice} className="flex items-end gap-2">
              <select
                aria-label={t('ejercicios.cuenta')}
                value={renglon.cuentaId}
                onChange={(evento) => {
                  const copia = [...renglones];
                  copia[indice] = { ...renglon, cuentaId: evento.target.value };
                  setRenglones(copia);
                }}
                className="min-w-0 flex-1 rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              >
                <option value="">{t('ejercicios.elegirCuenta')}</option>
                {imputables.map((cuenta) => (
                  <option key={cuenta.id} value={cuenta.id}>
                    {cuenta.codigo} {cuenta.nombre}
                  </option>
                ))}
              </select>
              <select
                aria-label={t('ejercicios.lado')}
                value={renglon.lado}
                onChange={(evento) => {
                  const copia = [...renglones];
                  copia[indice] = { ...renglon, lado: evento.target.value as 'debe' | 'haber' };
                  setRenglones(copia);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              >
                <option value="debe">{t('libros.debe')}</option>
                <option value="haber">{t('libros.haber')}</option>
              </select>
              <input
                aria-label={t('libros.importe')}
                inputMode="decimal"
                value={renglon.importe}
                onChange={(evento) => {
                  const copia = [...renglones];
                  copia[indice] = { ...renglon, importe: evento.target.value };
                  setRenglones(copia);
                }}
                className="w-28 rounded-marco border border-borde bg-superficie px-2 py-1 text-right text-sm text-texto numero"
              />
            </div>
          ))}

          <Boton
            onClick={() => {
              setRenglones([...renglones, { cuentaId: '', lado: 'debe', importe: '' }]);
            }}
          >
            {t('ejercicios.otroRenglon')}
          </Boton>

          {/* Que cuadre, a la vista y mientras se escribe. El servidor lo rechaza igual, pero
              descubrirlo después de cargar seis renglones es la forma más rápida de que alguien
              deje de usar esta pantalla. */}
          <p className="flex items-center justify-between text-xs">
            <span className="text-texto-tenue">
              {t('libros.debe')} {(totalDebe / 100).toFixed(2)} · {t('libros.haber')}{' '}
              {(totalHaber / 100).toFixed(2)}
            </span>
            <Insignia tono={cuadra ? 'bien' : 'atencion'}>
              {cuadra ? t('libros.cuadra') : t('libros.noCuadra')}
            </Insignia>
          </p>

          {asentar.isError ? (
            <p className="text-xs text-mal">{textoDeError(asentar.error)}</p>
          ) : null}
        </div>
      </Dialogo>

      <Dialogo
        abierto={reabriendo !== null}
        titulo={t('ejercicios.reabrir')}
        descripcion={t('ejercicios.reabrirAyuda')}
        onCerrar={() => {
          setReabriendo(null);
        }}
        acciones={
          <Boton
            tono="peligro"
            cargando={reabrir.isPending}
            disabled={motivo.trim() === ''}
            onClick={() => {
              if (reabriendo !== null) reabrir.mutate(reabriendo.id);
            }}
          >
            {t('ejercicios.reabrir')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-texto-tenue">
            {t('ejercicios.motivo')}
            <input
              value={motivo}
              onChange={(evento) => {
                setMotivo(evento.target.value);
              }}
              className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
            />
          </label>
          {reabrir.isError ? (
            <p className="text-xs text-mal">{textoDeError(reabrir.error)}</p>
          ) : null}
        </div>
      </Dialogo>
    </section>
  );
}
